import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { TopBar } from "@/components/Layout";
import { WizardHeader, WizardFooter } from "@/components/WizardShell";
import Step1Profile, { step1Valid } from "@/pages/steps/Step1Profile";
import Step2Company, { step2Valid } from "@/pages/steps/Step2Company";
import Step3Architecture, { step3Valid } from "@/pages/steps/Step3Architecture";
import Step4Hygiene, { step4Valid } from "@/pages/steps/Step4Hygiene";
import Step5Monitoring, { step5Valid } from "@/pages/steps/Step5Monitoring";
import Step6Compliance, { step6Valid } from "@/pages/steps/Step6Compliance";
import Step7Roi, { step7Valid } from "@/pages/steps/Step7Roi";
import Step8LeadGate, { step8Valid } from "@/pages/steps/Step8LeadGate";
import { useWizardStore } from "@/lib/store";
import { fetchReferenceData, submitStudy, fetchResume } from "@/lib/api";
import { translateRefData } from "@/i18n/refdata";

const STEPS_KEYS = [
  { key: "profile", Comp: Step1Profile, validate: step1Valid },
  { key: "company", Comp: Step2Company, validate: step2Valid },
  { key: "architecture", Comp: Step3Architecture, validate: step3Valid },
  { key: "hygiene", Comp: Step4Hygiene, validate: step4Valid },
  { key: "monitoring", Comp: Step5Monitoring, validate: step5Valid },
  { key: "compliance", Comp: Step6Compliance, validate: step6Valid },
  { key: "roi", Comp: Step7Roi, validate: step7Valid },
  { key: "lead", Comp: null, validate: null },
];

function scrollTop() { try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { window.scrollTo(0, 0); } }

export default function Wizard() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").split("-")[0];
  const STEPS = STEPS_KEYS.map((s, i) => ({
    ...s,
    title: t(`wizard.steps.s${i + 1}_title`),
    subtitle: t(`wizard.steps.s${i + 1}_sub`),
  }));
  const [params, setParams] = useSearchParams();
  const [refData, setRefData] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { inputs, updateSection, setInputs } = useWizardStore();

  const [lead, setLead] = useState({
    firstName: "", lastName: "", email: "",
    company: "", consent: false,
  });

  useEffect(() => {
    fetchReferenceData().then((rd) => setRefData(translateRefData(rd, lang))).catch(() => toast.error("Impossible de charger les données de référence."));
  }, [lang]);

  useEffect(() => {
    const token = params.get("reprise");
    if (token) {
      fetchResume(token).then((data) => {
        if (data?.inputs) setInputs(data.inputs);
        if (data?.lead) setLead({ ...data.lead, consent: false });
        toast.success(t("wizard.resumed"));
        params.delete("reprise");
        setParams(params, { replace: true });
      }).catch(() => toast.error(t("wizard.resume_invalid")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = STEPS[step];
  const canNext = useMemo(() => {
    if (!refData) return false;
    if (step === STEPS.length - 1) return step8Valid(lead);
    const val = inputs[current.key];
    if (current.key === "hygiene") return step4Valid(val, refData.hygiene_questions);
    if (current.key === "monitoring") return step5Valid(val, refData.monitoring_questions);
    return current.validate(val);
  }, [step, inputs, lead, refData, current]);

  const onNext = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      scrollTop();
      return;
    }
    // Submit
    setSubmitting(true);
    try {
      const study = await submitStudy({ inputs, lead });
      sessionStorage.setItem("ariovis_ad_result", JSON.stringify(study));
      navigate("/resultats");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du calcul de votre étude.");
    } finally {
      setSubmitting(false);
    }
  };

  const onBack = () => {
    if (step === 0) { navigate("/"); return; }
    setStep(step - 1);
    scrollTop();
  };

  if (!refData) return <div className="min-h-screen bg-slate-50"><TopBar /><div className="p-10 text-slate-500">{t("wizard.loading")}</div></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopBar />
      <WizardHeader step={step} total={STEPS.length} title={current.title} subtitle={current.subtitle} />
      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {step < 7 && (
                <current.Comp
                  ref_data={refData}
                  value={inputs[current.key]}
                  onChange={(patch) => updateSection(current.key, patch)}
                />
              )}
              {step === 7 && (
                <Step8LeadGate inputs={inputs} lead={lead} setLead={setLead} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <WizardFooter
        onBack={onBack}
        onNext={onNext}
        canNext={canNext}
        loading={submitting}
        nextLabel={step === STEPS.length - 1 ? t("wizard.submit") : t("wizard.next")}
        backLabel={t("wizard.back")}
      />
    </div>
  );
}
